import { isPresent } from "@/lib/utils"
import vuexStore from "@/store"
import { MutationPayload } from "vuex"
import { TinyEmitter } from "tiny-emitter"

export const MVT_EVENTS = {
  PAGE_VIEW: "page_view",
  LOGIN: "login",
  LOGGED_IN: "loggedIn",
  LOGGED_OUT: "loggedOut",
  SIGN_UP: "sign_up",
  FORM_SUBMIT: "form_submit",
  SEARCH: "search",
  BUTTON_CLICK: "button_click",
  CONTENT_ERROR: "content_error",
  NAVIGATION_ITEM_CLICK: "navigation_item_click",
  LINK_CLICK: "link_click",
  FEED_JOIN: "feed_join",
  FILE_DOWNLOAD: "file_download",
  TELEPHONE_CLICK: "telephone_click",
  EMAIL_CLICK: "email_click",
  VIDEO_START: "video_start",
  VIDEO_WATCH: "video_watch",
  SOCIAL_CLICK: "social_click",
  PRODUCT_PURCHASED: "product_purchased",
  PROPS_UPDATED: "propsUpdated",
  USER_PROPERTIES_CHANGED: "userProperties:changed",
  USER_PROPERTIES_ERROR: "userProperties:error",
  PRODUCTS_CHANGED: "products:changed",
  ACCESS_CHANGED: "access:changed",
  PROGRESS_CHANGED: "progress:changed",
  PROGRESS_COMPLETED: "progress:completed",
  SCHEDULE_CHANGED: "schedule:changed",
  SAVED_CHANGED: "saved:changed",
  ROUTE_CHANGED: "routeChanged"
} as const

const MUTATION_EVENTS: Record<string, string> = {
  "pages/setProgress": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setProgresses": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setPageProgress": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setPageCompletion": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setPageCompletions": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/deletePageProgress": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setPageSubscription": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/deletePageSubscription": MVT_EVENTS.PROGRESS_CHANGED,
  "pages/setPageIsBookmarked": MVT_EVENTS.SAVED_CHANGED,
  "pages/setBookmarkedPages": MVT_EVENTS.SAVED_CHANGED
}

export class EventManager {
  private readonly emitter: TinyEmitter
  private readonly unwatchers: Array<() => void> = []

  LOGGED_IN = MVT_EVENTS.LOGGED_IN
  LOGGED_OUT = MVT_EVENTS.LOGGED_OUT
  PROPS_UPDATED = MVT_EVENTS.PROPS_UPDATED

  constructor() {
    this.emitter = new TinyEmitter()
    this._initializeAuthWatcher()
    this._initializeUserPropertiesWatcher()
    this._initializeProductsWatcher()
    this._initializeMutationWatcher()
  }

  on(event: string, callback: (...args: any[]) => void, ctx?: any): this {
    this.emitter.on(event, callback, ctx)
    return this
  }

  once(event: string, callback: (...args: any[]) => void, ctx?: any): this {
    this.emitter.once(event, callback, ctx)
    return this
  }

  off(event: string, callback?: (...args: any[]) => void): this {
    if (callback) {
      this.emitter.off(event, callback)
    } else {
      this.emitter.off(event)
    }
    return this
  }

  emit(event: string, ...args: any[]): this {
    this.emitter.emit(event, ...args)
    return this
  }

  destroy(): void {
    this.unwatchers.forEach(unwatcher => unwatcher())
  }

  private _initializeAuthWatcher(): void {
    // Watch the member id, not the whole user object: the auth store rewrites
    // state.user with a fresh reference on benign same-member updates (avatar,
    // name, product ids), which would otherwise re-emit LOGGED_IN on every such
    // change. The id transitions only on a real login / logout / member switch.
    const unwatcher = vuexStore.watch(
      (_: any, getters: { [key: string]: any }) => {
        const user = getters["auth/currentUser"]
        return isPresent(user) ? user.id : null
      },
      (id: string | null) => {
        const user = vuexStore.getters["auth/currentUser"]
        this.emit(
          isPresent(id) ? MVT_EVENTS.LOGGED_IN : MVT_EVENTS.LOGGED_OUT,
          isPresent(user) ? user : null
        )
      }
    )
    this.unwatchers.push(unwatcher)
  }

  private _initializeUserPropertiesWatcher(): void {
    const unwatcher = vuexStore.watch(
      (_: any, getters: { [key: string]: any }) =>
        getters["subscriberProperties/subscriberSubmittedProperties"],
      (properties: Record<string, unknown>) =>
        this.emit(MVT_EVENTS.USER_PROPERTIES_CHANGED, properties)
    )
    this.unwatchers.push(unwatcher)
  }

  private _initializeProductsWatcher(): void {
    // Ownership changes drive a code block's re-render. Compare the joined id
    // string rather than the array, whose reference churns without a value
    // change.
    const unwatcher = vuexStore.watch(
      (_: any, getters: { [key: string]: any }) =>
        (getters["auth/currentUser"]?.activeProductIds || []).join(","),
      () => this.emit(MVT_EVENTS.PRODUCTS_CHANGED)
    )
    this.unwatchers.push(unwatcher)
  }

  // Emit on the mutations that move what the reactive reads see.
  private _initializeMutationWatcher(): void {
    const unsubscribe = vuexStore.subscribe((mutation: MutationPayload) => {
      const event = MUTATION_EVENTS[mutation.type]
      if (event) this.emit(event, mutation)
    })
    this.unwatchers.push(unsubscribe)
  }
}
